import { useState, useEffect, useCallback } from 'react';
import { Order, OrderFilter } from '../types/orders';
import { fetchOrders, fetchOrderStatusCounts } from '../services/ordersApi';
import { fetchSyncStatus as fetchSyncStatusApi, syncOrders as syncOrdersApi, syncSingleOrder as syncSingleOrderApi, type SyncOrdersResponse, type SyncSingleOrderResponse } from '../services/b2bSyncApi';

interface UseOrdersOptions {
  filter?: OrderFilter;
  search?: string;
  autoSync?: boolean;
  includeHidden?: boolean;
}

interface UseOrdersReturn {
  orders: Order[];
  loading: boolean;
  error: string;
  syncing: boolean;
  syncStatus: any;
  statusCounts: Record<string, number>;
  autoSyncDone: boolean;
  fetchOrders: () => Promise<void>;
  refreshStatusCounts: () => Promise<void>;
  handleSyncOrders: () => Promise<SyncOrdersResponse | undefined>;
  handleSyncSingleOrder: (orderNumber: string) => Promise<SyncSingleOrderResponse | undefined>;
  handleSearch: (searchTerm: string) => void;
  handleFilterChange: (filter: OrderFilter) => void;
}

export const useOrders = (options: UseOrdersOptions = {}): UseOrdersReturn => {
  const { filter: initialFilter = 'pending', search: initialSearch = '', autoSync = true, includeHidden = false } = options;
  
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [filter, setFilter] = useState<OrderFilter>(initialFilter);
  const [syncStatus, setSyncStatus] = useState<any>(null);
  const [autoSyncDone, setAutoSyncDone] = useState(false);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});

  // Sync internal filter state when prop changes
  useEffect(() => {
    setFilter(initialFilter);
  }, [initialFilter]);

  const loadSyncStatus = useCallback(async () => {
    try {
      console.debug('[useOrders] loadSyncStatus: start');
      const data = await fetchSyncStatusApi();
      if (data.success) {
        setSyncStatus(data.syncStatus.orders);
        console.debug('[useOrders] loadSyncStatus: success', data.syncStatus?.orders);
      }
    } catch (err) {
      console.error('Error fetching sync status:', err);
    }
  }, []);

  const loadStatusCounts = useCallback(async () => {
    try {
      console.debug('[useOrders] loadStatusCounts: start');
      const counts = await fetchOrderStatusCounts();
      setStatusCounts(counts);
      console.debug('[useOrders] loadStatusCounts: success', counts);
    } catch (err) {
      console.error('Error fetching status counts:', err);
    }
  }, []);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      console.debug('[useOrders] loadOrders: start', { filter, searchTerm, includeHidden });
      const data = await fetchOrders(filter, searchTerm || undefined, undefined, includeHidden || undefined);
      if (data.success) {
        setOrders(data.orders);
        console.debug('[useOrders] loadOrders: success', {
          count: data.orders?.length || 0,
          pagination: data.pagination
        });
      }
    } catch (err: any) {
      console.error('Error fetching orders:', err);
      setError(err.response?.data?.message || 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, [filter, searchTerm, includeHidden]);

  const checkAndAutoSync = useCallback(async () => {
    if (!autoSync) {
      setAutoSyncDone(true);
      return;
    }

    try {
      console.debug('[useOrders] checkAndAutoSync: checking existing orders');
      const data = await fetchOrders('all', '', 1);
      if (data.success && data.pagination?.total === 0) {
        console.log('No orders in database, skipping auto-sync');
        setAutoSyncDone(true);
      } else {
        console.debug('[useOrders] checkAndAutoSync: existing orders found, skipping auto-sync', { total: data.pagination?.total });
        setAutoSyncDone(true);
      }
    } catch (err) {
      console.error('Error checking for auto-sync:', err);
      setAutoSyncDone(true);
      setLoading(false);
    }
  }, [autoSync]);

  const handleSyncOrders = useCallback(async () => {
    let pollInterval: NodeJS.Timeout | null = null;
    
    try {
      console.debug('[useOrders] handleSyncOrders: start');
      setSyncing(true);
      setError('');
      
      // Start polling for progress
      pollInterval = setInterval(async () => {
        try {
          const data = await fetchSyncStatusApi();
          if (data.success && data.syncStatus.orders) {
            setSyncStatus(data.syncStatus.orders);
            
            // Stop polling if sync is complete
            if (data.syncStatus.orders.status !== 'in_progress') {
              if (pollInterval) clearInterval(pollInterval);
            }
          }
        } catch (err) {
          console.error('Error polling sync status:', err);
        }
      }, 500); // Poll every 500ms
      
      const data = await syncOrdersApi();
      
      if (pollInterval) clearInterval(pollInterval);
      
      if (data.success) {
        setAutoSyncDone(true);
        await loadSyncStatus();
        await loadStatusCounts();
        await loadOrders();
        console.debug('[useOrders] handleSyncOrders: success', data);
      }
      return data;
    } catch (err: any) {
      console.error('Error syncing orders:', err);
      if (pollInterval) clearInterval(pollInterval);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to sync orders');
      setAutoSyncDone(true);
    } finally {
      setSyncing(false);
    }
  }, [loadSyncStatus, loadStatusCounts, loadOrders]);

  const handleSyncSingleOrder = useCallback(async (orderNumber: string) => {
    try {
      console.debug('[useOrders] handleSyncSingleOrder: start', orderNumber);
      setError('');
      const data = await syncSingleOrderApi(orderNumber);
      if (data.success) {
        await loadSyncStatus();
        await loadStatusCounts();
        await loadOrders();
        console.debug('[useOrders] handleSyncSingleOrder: success', data);
      }
      return data;
    } catch (err: any) {
      console.error('Error syncing single order:', err);
      const message = err.response?.data?.message || err.response?.data?.error || 'Failed to sync order';
      setError(message);
      return { success: false, error: message } as SyncSingleOrderResponse;
    }
  }, [loadSyncStatus, loadStatusCounts, loadOrders]);

  useEffect(() => {
    const initialize = async () => {
      await loadSyncStatus();
      await checkAndAutoSync();
      await loadStatusCounts();
    };
    initialize();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (autoSyncDone && !syncing) {
      loadOrders();
    }
  }, [filter, searchTerm, autoSyncDone, syncing, loadOrders]);

  const handleSearch = useCallback((search: string) => {
    setSearchTerm(search);
  }, []);

  const handleFilterChange = useCallback((newFilter: OrderFilter) => {
    setFilter(newFilter);
  }, []);

  return {
    orders,
    loading,
    error,
    syncing,
    syncStatus,
    statusCounts,
    autoSyncDone,
    fetchOrders: loadOrders,
    refreshStatusCounts: loadStatusCounts,
    handleSyncOrders,
    handleSyncSingleOrder,
    handleSearch,
    handleFilterChange
  };
};

